// Phase 9B.3b.3 — Flat readable day schedule.
//
// Rebuild premise (session 1 MEGA slice):
//   * ONE row per canonical shift record — never a "group card" that hides
//     20 real records behind a fake single element.
//   * Full-width rows. More employees = more rows, NEVER thinner cards.
//   * Each row shows exactly the five things spec §11 demands:
//       TIME · EMPLOYEE · FUNCTION · LOCATION
//     plus OPEN / CONFLICT / DRAFT badges when relevant.
//   * Row click opens the centered edit modal for THAT individual shift.
//   * Optional collapse only for very large blocks of truly identical
//     work (>=10 records identical in start, end, location, team, role,
//     employee=null-or-assigned). Collapse is calm and expandable inline.
//   * No time axis on the left, no absolute positioning, no cascade math.
//     Chronological vertical list. Vertical scroll is fine.
import { useMemo, useState } from "react";
import { AlertTriangle, UserPlus, ChevronDown, ChevronRight, Plus } from "lucide-react";

const COLLAPSE_THRESHOLD = 10;

function toMin(h) { const [a, b] = (h || "00:00").split(":").map(Number); return a * 60 + (b || 0); }
function normEnd(s, e) { let a = toMin(s); let b = toMin(e); if (b <= a) b += 1440; return { a, b }; }

// Cluster shifts by identical signature so we can OPTIONALLY collapse
// blocks with >= COLLAPSE_THRESHOLD records. Everything else stays flat.
function cluster(shifts) {
  const map = new Map();
  for (const s of shifts) {
    const key = [
      s.start_time || "",
      s.end_time || "",
      s.location_id || "",
      s.team_id || "",
      s.role_label || "",
      s.employee_id ? "A" : "O",
    ].join("|");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  const arr = [];
  for (const [key, list] of map.entries()) {
    arr.push({ key, list });
  }
  arr.sort((a, b) => toMin(a.list[0].start_time) - toMin(b.list[0].start_time));
  return arr;
}

function detectConflicts(shifts) {
  const set = new Set();
  const byEmp = new Map();
  for (const s of shifts) {
    if (!s.employee_id) continue;
    (byEmp.get(s.employee_id) || byEmp.set(s.employee_id, []).get(s.employee_id)).push(s);
  }
  for (const list of byEmp.values()) {
    if (list.length < 2) continue;
    for (let i = 0; i < list.length; i++)
      for (let j = i + 1; j < list.length; j++) {
        const A = normEnd(list[i].start_time, list[i].end_time);
        const B = normEnd(list[j].start_time, list[j].end_time);
        if (Math.max(A.a, B.a) < Math.min(A.b, B.b)) { set.add(list[i]._id); set.add(list[j]._id); }
      }
  }
  return set;
}

export default function DayAgenda({
  selectedDate, dayShifts, employeesById, locations, teams,
  onOpenShift, onOpenNew, t,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const conflictSet = useMemo(() => detectConflicts(dayShifts), [dayShifts]);
  const clusters = useMemo(() => cluster(dayShifts), [dayShifts]);

  const locName = (id) => locations.find((l) => l._id === id)?.name || "";
  const locColor = (id) => locations.find((l) => l._id === id)?.color || "hsl(var(--primary))";
  const teamName = (id) => teams.find((tm) => tm._id === id)?.name || "";

  const parsedDate = selectedDate ? new Date(selectedDate + "T12:00:00") : null;
  const headerTitle = parsedDate
    ? parsedDate.toLocaleDateString("nl", { weekday: "long", day: "numeric", month: "long" })
    : "";

  const uniqueEmps = new Set();
  let openCount = 0;
  for (const s of dayShifts) {
    if (s.employee_id) uniqueEmps.add(s.employee_id);
    else openCount += 1;
  }
  const scheduledCount = uniqueEmps.size;

  const toggle = (k) => setExpanded((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  return (
    <div className="card overflow-hidden" data-testid="day-agenda">
      <div className="px-4 sm:px-5 py-3 border-b hairline flex items-baseline gap-3 flex-wrap">
        <div className="font-display text-[17px] sm:text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))] capitalize">
          {headerTitle}
        </div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] tabular-nums flex items-center gap-2">
          {scheduledCount > 0 && (
            <span data-testid="day-agenda-scheduled-count">
              <span className="font-bold text-[hsl(var(--text-strong))]">{scheduledCount}</span>{" "}
              {scheduledCount === 1 ? (t("employee_single") || "medewerker") : (t("employee_many") || "medewerkers")}
            </span>
          )}
          {openCount > 0 && (
            <>
              <span className="text-[hsl(var(--text-soft))]">·</span>
              <span
                className="px-2 py-0.5 rounded-full font-bold text-[11px]"
                style={{ background: "hsl(var(--warning-soft))", color: "hsl(var(--warning))" }}
                data-testid="day-agenda-open-count"
              >
                {openCount} {openCount === 1 ? (t("open_shift_single") || "open dienst") : (t("open_shifts") || "open diensten")}
              </span>
            </>
          )}
          {scheduledCount === 0 && openCount === 0 && (
            <span className="italic text-[hsl(var(--text-muted))]">{t("empty_day") || "leeg"}</span>
          )}
        </div>
      </div>

      {clusters.length === 0 ? (
        <div className="py-14 grid place-items-center" data-testid="day-agenda-empty">
          <div className="text-center max-w-md px-6">
            <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] mb-1">
              {t("day_agenda_empty_title") || "Nog geen diensten gepland voor deze dag."}
            </div>
            <button
              onClick={() => onOpenNew && onOpenNew(selectedDate)}
              className="btn btn-primary mt-3"
              data-testid="day-agenda-empty-add"
            >
              <Plus size={14} strokeWidth={2} />
              {t("add_shift") || "Dienst toevoegen"}
            </button>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-[hsl(var(--border))]" data-testid="day-agenda-rows">
          {clusters.flatMap(({ key, list }) => {
            const isOpen = !list[0].employee_id;
            // Collapse large identical clusters (>= threshold).
            if (list.length >= COLLAPSE_THRESHOLD && !expanded.has(key)) {
              const s0 = list[0];
              const names = list
                .map((s) => employeesById[s.employee_id]?.name)
                .filter(Boolean)
                .slice(0, 3);
              return [
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    data-testid={`day-agenda-cluster-${key}`}
                    className="w-full flex items-center gap-4 px-4 sm:px-5 py-3 text-left hover:bg-[hsl(var(--muted))]/40 focus-visible:bg-[hsl(var(--muted))]/60 outline-none transition-colors"
                  >
                    <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: locColor(s0.location_id) }} />
                    <span className="font-display font-bold tabular-nums text-[15px] text-[hsl(var(--text-strong))] w-[112px] shrink-0">
                      {s0.start_time}<span className="text-[hsl(var(--text-soft))] font-normal">–</span>{s0.end_time}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))]">
                        {list.length} {t("employee_many") || "medewerkers"}
                      </span>
                      {names.length > 0 && (
                        <span className="ml-2 text-[12.5px] text-[hsl(var(--text-muted))] truncate">
                          {names.join(" · ")}{list.length > names.length && ` · +${list.length - names.length}`}
                        </span>
                      )}
                    </span>
                    <span className="text-[12.5px] text-[hsl(var(--text-muted))] hidden sm:inline">
                      {s0.role_label || teamName(s0.team_id)}
                    </span>
                    <span className="text-[12.5px] font-semibold text-[hsl(var(--text))] hidden md:inline truncate max-w-[180px]">
                      {locName(s0.location_id)}
                    </span>
                    <ChevronRight size={14} className="text-[hsl(var(--text-muted))] shrink-0" />
                  </button>
                </li>,
              ];
            }
            // Expanded / small cluster: render each individual shift as its
            // own click-to-edit row.
            const rows = list.map((s) => {
              const isConflict = conflictSet.has(s._id);
              const isDraft = s.status !== "published";
              const shiftIsOpen = !s.employee_id;
              const emp = s.employee_id ? employeesById[s.employee_id] : null;
              return (
                <li key={s._id}>
                  <button
                    type="button"
                    onClick={() => onOpenShift && onOpenShift(s)}
                    data-testid={`day-agenda-shift-${s._id}`}
                    data-open={shiftIsOpen || undefined}
                    data-draft={isDraft || undefined}
                    data-conflict={isConflict || undefined}
                    className="w-full flex items-center gap-4 px-4 sm:px-5 py-3 text-left hover:bg-[hsl(var(--muted))]/40 focus-visible:bg-[hsl(var(--muted))]/60 outline-none transition-colors"
                  >
                    <span
                      className="w-1 self-stretch rounded-full shrink-0"
                      style={{ background: isConflict ? "hsl(var(--danger))" : shiftIsOpen ? "hsl(var(--warning))" : locColor(s.location_id) }}
                    />
                    <span className="font-display font-bold tabular-nums text-[15px] text-[hsl(var(--text-strong))] w-[112px] shrink-0">
                      {s.start_time}<span className="text-[hsl(var(--text-soft))] font-normal">–</span>{s.end_time}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className={`text-[13.5px] font-semibold truncate ${shiftIsOpen ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--text-strong))]"}`}>
                          {shiftIsOpen ? (t("open_shift_label") || "Open dienst") : emp?.name}
                        </span>
                        {isConflict && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "hsl(var(--danger)/0.15)", color: "hsl(var(--danger))" }}>
                            <AlertTriangle size={10} strokeWidth={2.6} />{t("overlap") || "Overlap"}
                          </span>
                        )}
                        {shiftIsOpen && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "hsl(var(--warning)/0.15)", color: "hsl(var(--warning))" }}>
                            <UserPlus size={10} strokeWidth={2.6} />{t("open_shift_badge") || "OPEN"}
                          </span>
                        )}
                        {isDraft && !shiftIsOpen && !isConflict && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ background: "hsl(var(--muted))", color: "hsl(var(--text-muted))" }}>
                            {t("unpublished_state") || "concept"}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-[12.5px] text-[hsl(var(--text-muted))] hidden sm:inline truncate max-w-[180px]">
                      {s.role_label || teamName(s.team_id)}
                    </span>
                    <span className="text-[12.5px] font-semibold text-[hsl(var(--text))] hidden md:inline truncate max-w-[220px]">
                      {locName(s.location_id)}
                    </span>
                  </button>
                </li>
              );
            });
            // Add "collapse back" affordance when expanded.
            if (list.length >= COLLAPSE_THRESHOLD) {
              rows.push(
                <li key={`${key}-collapse`}>
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    data-testid={`day-agenda-cluster-collapse-${key}`}
                    className="w-full px-4 sm:px-5 py-2 text-[11.5px] font-semibold text-[hsl(var(--primary))] hover:underline text-left inline-flex items-center gap-1"
                  >
                    <ChevronDown size={12} className="rotate-180" />{t("collapse") || "Inklappen"}
                  </button>
                </li>
              );
            }
            return rows;
          })}
        </ul>
      )}
    </div>
  );
}
